import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Sun, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { useTheme } from "@/context/ThemeContext";

const NAV_LINKS = [
  { to: "/qui-sommes-nous", label: "Qui sommes nous ?" },
  { to: "/realisations", label: "Réalisations" },
  { to: "/blog", label: "Blog" },
];

export const goToContact = (navigate, location) => {
  if (location.pathname === "/") {
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  } else {
    navigate("/#contact");
  }
};

const MinimalHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { mode, toggleMode } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const isActive = (to) =>
    location.pathname === to || location.pathname.startsWith(`${to}/`);

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 bg-background/85 backdrop-blur-md border-b border-border/50">
        <div className="max-w-5xl mx-auto px-6 h-16 grid grid-cols-[auto_1fr_auto] items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-xl font-bold tracking-tight">
            <Logo className="w-7 h-7" />
            <span>Lacrea</span>
          </Link>

          <nav className="hidden sm:flex items-center justify-center gap-5">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-semibold transition-colors ${
                  isActive(link.to) ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 justify-self-end">
            <button
              type="button"
              onClick={toggleMode}
              aria-label={mode === "dark" ? "Passer en mode clair" : "Passer en mode sombre"}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-border text-foreground active:bg-muted sm:hover:bg-muted transition-colors"
            >
              {mode === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <Button
              onClick={() => goToContact(navigate, location)}
              className="hidden sm:inline-flex rounded-full bg-primary hover:bg-primary/90 px-5 h-10 text-sm font-semibold"
            >
              Demander un devis
            </Button>
            <button
              type="button"
              aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              className="sm:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-border text-foreground active:bg-muted"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      <div
        className={`sm:hidden fixed inset-0 z-40 transition-opacity duration-200 ${
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!menuOpen}
      >
        <div
          className="absolute inset-0 bg-background"
          onClick={() => setMenuOpen(false)}
        />
        <nav className="relative flex flex-col pt-24 px-6 pb-8 gap-1 h-full overflow-y-auto">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMenuOpen(false)}
              className={`text-lg font-semibold py-4 border-b border-border/40 ${
                isActive(link.to) ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Button
            onClick={() => {
              setMenuOpen(false);
              goToContact(navigate, location);
            }}
            className="rounded-full bg-primary hover:bg-primary/90 h-12 text-base font-semibold mt-8"
          >
            Demander un devis
          </Button>
        </nav>
      </div>
    </>
  );
};

export default MinimalHeader;
